import React, { useState } from 'react'
import { alsokTheme, componentThemes, uiTokens, themeHelpers } from '../styles/alsok-theme'

const LoginScreen = ({ onLogin, onReceptionStatusChange }) => {
  const [loginForm, setLoginForm] = useState({
    username: '',
    password: ''
  })
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [showTestAccounts, setShowTestAccounts] = useState(false)
  const [showReceptionModeDialog, setShowReceptionModeDialog] = useState(false)

  // テスト用アカウント設定
  const testAccounts = [
    { 
      username: 'admin', 
      password: 'admin123', 
      role: 'administrator',
      name: '管理者'
    }
  ]

  const handleLogin = async (e) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')

    try {
      // ローカル認証（実際の運用ではサーバー認証を推奨）
      const user = testAccounts.find(
        account => 
          account.username === loginForm.username && 
          account.password === loginForm.password
      )

      if (user) {
        // 認証成功の演出
        await new Promise(resolve => setTimeout(resolve, 1000))
        
        // 管理者ログイン時は受付状態を変更しない
        console.log('🔑 管理者ログイン: 受付状態は変更しません')
        
        onLogin({
          username: user.username,
          role: user.role,
          name: user.name,
          loginTime: new Date().toISOString()
        })
      } else {
        setError('ユーザー名またはパスワードが正しくありません')
      }
    } catch (err) {
      setError('ログイン処理中にエラーが発生しました')
    } finally {
      setIsLoading(false)
    }
  }

  const handleTestAccountSelect = (account) => {
    setLoginForm({
      username: account.username,
      password: account.password
    })
    setShowTestAccounts(false)
  }

  const handleQuickReceptionMode = async () => {
    setShowReceptionModeDialog(true)
  }

  const handleConfirmReceptionMode = () => {
    setShowReceptionModeDialog(false)
    
    console.log('🚀 受付モード: ゲストモードでログインしました（稼働状態は変更されません）')
    
    // ゲストモード（受付専用）でログイン
    onLogin({
      username: 'guest',
      role: 'reception-only',
      name: 'ゲストユーザー',
      loginTime: new Date().toISOString()
    })
  }

  const handleCancelReceptionMode = () => {
    setShowReceptionModeDialog(false)
  }

  return (
    <div className="login-screen">
      <div className="login-container">
        <div className="login-header">
          <div className="logo">
            <div className="logo-icon">🛡️</div>
            <div className="logo-title">
              <span className="logo-brand">ALSOK</span>
              <span className="logo-product">AI受付まもりちゃん</span>
            </div>
            <p className="logo-subtitle">Secure Reception & Management System</p>
          </div>
          <div className="header-particles">
            <div className="particle particle-1"></div>
            <div className="particle particle-2"></div>
            <div className="particle particle-3"></div>
            <div className="particle particle-4"></div>
            <div className="particle particle-5"></div>
          </div>
        </div>

        <div className="login-form-container">
          <form onSubmit={handleLogin} className="login-form">
            <h2>🔐 システムログイン</h2>
            
            <div className="form-group">
              <label>
                <span className="label-icon">👤</span>
                ユーザー名
              </label>
              <div className="input-wrapper">
                <input
                  type="text"
                  value={loginForm.username}
                  onChange={(e) => setLoginForm(prev => ({ ...prev, username: e.target.value }))}
                  placeholder="ユーザー名を入力"
                  required
                  autoComplete="username"
                />
                <div className="input-icon">👤</div>
              </div>
            </div>

            <div className="form-group">
              <label>
                <span className="label-icon">🔑</span>
                パスワード
              </label>
              <div className="input-wrapper">
                <input
                  type="password"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm(prev => ({ ...prev, password: e.target.value }))}
                  placeholder="パスワードを入力"
                  required
                  autoComplete="current-password"
                />
                <div className="input-icon">🔑</div>
              </div>
            </div>

            {error && (
              <div className="error-message">
                ⚠️ {error}
              </div>
            )}

            <button 
              type="submit" 
              disabled={isLoading || !loginForm.username || !loginForm.password}
              className={`login-button ${isLoading ? 'loading' : ''}`}
            >
              <span className="button-content">
                <span className="button-icon">
                  {isLoading ? '🔄' : '🚀'}
                </span>
                <span className="button-text">
                  {isLoading ? '認証中...' : 'ログイン'}
                </span>
              </span>
              {isLoading && <div className="loading-overlay"></div>}
            </button>
          </form>

          <div className="test-accounts-section">
            <button 
              type="button"
              onClick={() => setShowTestAccounts(!showTestAccounts)}
              className="test-accounts-toggle"
            >
              🧪 テスト用アカウント {showTestAccounts ? '▲' : '▼'}
            </button>

            {showTestAccounts && (
              <div className="test-accounts-list">
                {testAccounts.map(account => (
                  <button
                    key={account.username}
                    onClick={() => handleTestAccountSelect(account)}
                    className="test-account-item"
                  >
                    <span className="account-name">{account.name}</span>
                    <span className="account-role">({account.role})</span>
                    <span className="account-credentials">
                      {account.username} / {account.password}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="quick-access">
            <div className="quick-access-header">
              <h3>⚡ クイックアクセス</h3>
              <p className="quick-access-description">
                管理機能なしで受付機能のみ利用
              </p>
            </div>
            <button 
              onClick={handleQuickReceptionMode}
              className="quick-reception-button"
            >
              <span className="button-main-text">受付モード</span>
              <span className="button-sub-text">今すぐ開始</span>
            </button>
            <p className="quick-access-note">
              💡 ログイン不要で受付業務を開始できます
            </p>
          </div>
        </div>

        <div className="login-footer">
          <p>© 2025 ALSOK セキュリティ</p>
        </div>
      </div>

      {/* カスタム確認ダイアログ */}
      {showReceptionModeDialog && (
        <div className="modal-overlay">
          <div className="modal-container">
            <div className="modal-header">
              <h2 className="modal-title">受付モード開始確認</h2>
            </div>
            
            <div className="modal-content">
              <p className="modal-description">
                「受付モード」を開始しますか？
              </p>
              
              <div className="modal-warning">
                <div className="warning-icon">⚠️</div>
                <div className="warning-title">注意事項</div>
              </div>
              
              <ul className="modal-notes">
                <li>お客様用の受付モードに遷移します</li>
                <li>管理機能は利用できません</li>
                <li>システムの稼働状態は変更されません</li>
                <li>管理者モードに戻るには再ログインが必要です</li>
              </ul>
            </div>
            
            <div className="modal-actions">
              <button 
                onClick={handleCancelReceptionMode}
                className="modal-button cancel-button"
              >
                <span className="button-icon">❌</span>
                <span>キャンセル</span>
              </button>
              <button 
                onClick={handleConfirmReceptionMode}
                className="modal-button confirm-button"
              >
                <span className="button-icon">✅</span>
                <span>開始する</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx="true">{`
        .login-screen {
          min-height: 100vh;
          background: linear-gradient(135deg, 
            ${alsokTheme.primary.dark} 0%, 
            ${alsokTheme.primary.main} 35%, 
            ${alsokTheme.secondary.main} 70%, 
            ${alsokTheme.secondary.light} 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          font-family: ${alsokTheme.typography.fontFamily.primary};
          padding: ${alsokTheme.spacing.lg};
          position: relative;
          overflow: hidden;
        }
        
        .login-screen::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse"><path d="M 10 0 L 0 0 0 10" fill="none" stroke="rgba(255,255,255,0.08)" stroke-width="0.5"/></pattern></defs><rect width="100" height="100" fill="url(%23grid)"/></svg>');
          animation: gridMove 20s linear infinite;
          pointer-events: none;
        }

        .login-screen::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: radial-gradient(circle at 30% 20%, rgba(255,255,255,0.1) 0%, transparent 50%),
                      radial-gradient(circle at 70% 80%, rgba(255,255,255,0.05) 0%, transparent 50%);
          animation: glowMove 15s ease-in-out infinite;
          pointer-events: none;
        }

        .login-container {
          background: ${componentThemes.login.card.background};
          border-radius: ${componentThemes.login.card.borderRadius};
          box-shadow: ${componentThemes.login.card.shadow};
          overflow: hidden;
          max-width: 600px;
          width: 100%;
          backdrop-filter: blur(15px);
          border: 2px solid ${alsokTheme.primary.light};
          position: relative;
          z-index: 1;
          min-height: 700px;
        }

        .login-header {
          background: linear-gradient(135deg, 
            rgba(27, 54, 93, 0.95) 0%, 
            rgba(46, 75, 105, 0.9) 50%, 
            rgba(74, 144, 164, 0.85) 100%);
          color: ${alsokTheme.neutral.white};
          padding: 50px 40px;
          text-align: center;
          position: relative;
          overflow: hidden;
          backdrop-filter: blur(10px);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 200px;
        }
        
        .login-header::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, 
            ${alsokTheme.accent.orange} 0%, 
            ${alsokTheme.accent.success} 50%, 
            ${alsokTheme.accent.orange} 100%);
          animation: borderGlow 3s ease-in-out infinite;
        }

        .logo {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          width: 100%;
          text-align: center;
        }

        .logo-icon {
          font-size: 80px;
          margin-bottom: ${alsokTheme.spacing.md};
          animation: securityPulse 3s ease-in-out infinite;
          text-shadow: 0 4px 12px rgba(0,0,0,0.4);
          filter: drop-shadow(0 0 20px rgba(255,255,255,0.3));
        }

        .logo-title {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
          margin-bottom: ${alsokTheme.spacing.sm};
        }

        .logo-brand {
          font-size: ${alsokTheme.typography.fontSize['4xl']};
          font-weight: ${alsokTheme.typography.fontWeight.bold};
          text-shadow: 0 3px 6px rgba(0,0,0,0.4);
          letter-spacing: 3px;
          background: linear-gradient(135deg, #ffffff 0%, #e8f4fd 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: textShimmer 4s ease-in-out infinite;
        }

        .logo-product {
          font-size: ${alsokTheme.typography.fontSize['2xl']};
          font-weight: ${alsokTheme.typography.fontWeight.semibold};
          text-shadow: 0 2px 4px rgba(0,0,0,0.3);
          letter-spacing: 1px;
          color: ${alsokTheme.neutral.white};
          opacity: 0.95;
        }

        .logo p {
          margin: 0;
          opacity: 0.9;
          font-size: ${alsokTheme.typography.fontSize.sm};
          font-weight: ${alsokTheme.typography.fontWeight.medium};
          letter-spacing: 1.5px;
          text-transform: uppercase;
        }

        .logo-subtitle {
          margin: 0;
          opacity: 0.9;
          font-size: 12px;
          font-weight: ${alsokTheme.typography.fontWeight.medium};
          letter-spacing: 1px;
          text-transform: uppercase;
          white-space: nowrap;
          text-align: center;
          width: 100%;
          display: block;
          position: relative;
          left: 0;
          right: 0;
          padding: 0;
          margin-left: auto;
          margin-right: auto;
        }

        .header-particles {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          pointer-events: none;
          overflow: hidden;
        }

        .particle {
          position: absolute;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 50%;
          animation: floatParticle 6s ease-in-out infinite;
        }

        .particle-1 {
          width: 8px;
          height: 8px;
          top: 20%;
          left: 15%;
          animation-delay: 0s;
          animation-duration: 8s;
        }

        .particle-2 {
          width: 12px;
          height: 12px;
          top: 60%;
          right: 20%;
          animation-delay: 2s;
          animation-duration: 10s;
        }

        .particle-3 {
          width: 6px;
          height: 6px;
          top: 30%;
          right: 30%;
          animation-delay: 4s;
          animation-duration: 7s;
        }

        .particle-4 {
          width: 10px;
          height: 10px;
          bottom: 25%;
          left: 25%;
          animation-delay: 1s;
          animation-duration: 9s;
        }

        .particle-5 {
          width: 4px;
          height: 4px;
          top: 45%;
          left: 60%;
          animation-delay: 3s;
          animation-duration: 6s;
        }


        .login-form-container {
          padding: 40px 50px;
        }

        .login-form h2 {
          text-align: center;
          margin: 0 0 ${alsokTheme.spacing.xl} 0;
          color: ${alsokTheme.primary.main};
          font-size: ${alsokTheme.typography.fontSize['2xl']};
          font-weight: ${alsokTheme.typography.fontWeight.bold};
          border-bottom: 2px solid ${alsokTheme.primary.light};
          padding-bottom: ${alsokTheme.spacing.md};
        }

        .form-group {
          margin-bottom: 28px;
        }

        .form-group label {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: ${alsokTheme.spacing.sm};
          font-weight: ${alsokTheme.typography.fontWeight.semibold};
          color: ${alsokTheme.primary.main};
          font-size: ${alsokTheme.typography.fontSize.sm};
        }

        .label-icon {
          font-size: 16px;
          opacity: 0.8;
        }

        .input-wrapper {
          position: relative;
          display: flex;
          align-items: center;
        }

        .input-icon {
          position: absolute;
          right: 12px;
          font-size: 16px;
          color: ${alsokTheme.neutral.mediumGray};
          pointer-events: none;
          transition: ${alsokTheme.animation.transition};
        }

        .form-group input {
          width: 100%;
          padding: 16px 45px 16px 16px;
          border: 2px solid ${componentThemes.login.input.border};
          border-radius: ${componentThemes.login.input.borderRadius};
          font-size: 16px;
          transition: ${alsokTheme.animation.transition};
          font-family: ${alsokTheme.typography.fontFamily.primary};
          background: ${componentThemes.login.input.background};
          box-shadow: inset 0 2px 4px rgba(0,0,0,0.05);
          min-height: 50px;
        }

        .form-group input:focus {
          outline: none;
          border-color: ${componentThemes.login.input.focus};
          box-shadow: 
            0 0 0 3px ${themeHelpers.withOpacity(alsokTheme.primary.main, 0.15)},
            inset 0 2px 4px rgba(0,0,0,0.05);
          background: ${themeHelpers.withOpacity(alsokTheme.primary.main, 0.02)};
        }

        .form-group input:focus + .input-icon {
          color: ${alsokTheme.primary.main};
          transform: scale(1.1);
        }

        .error-message {
          background: rgba(220,53,69,0.1);
          color: ${alsokTheme.status.error};
          padding: ${alsokTheme.spacing.md};
          border-radius: ${alsokTheme.borderRadius.md};
          margin-bottom: ${alsokTheme.spacing.lg};
          border-left: 4px solid ${alsokTheme.status.error};
          font-weight: ${alsokTheme.typography.fontWeight.medium};
        }

        .login-button {
          width: 100%;
          padding: 18px 24px;
          background: ${alsokTheme.gradients.primary};
          color: ${alsokTheme.neutral.white};
          border: 2px solid ${alsokTheme.primary.main};
          border-radius: ${alsokTheme.borderRadius.md};
          font-size: 18px;
          font-weight: ${componentThemes.login.button.fontWeight};
          cursor: pointer;
          transition: ${alsokTheme.animation.transition};
          font-family: ${alsokTheme.typography.fontFamily.primary};
          box-shadow: ${alsokTheme.shadows.md};
          position: relative;
          overflow: hidden;
          min-height: 56px;
          margin-top: 8px;
        }

        .button-content {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          position: relative;
          z-index: 2;
        }

        .button-icon {
          font-size: 18px;
          transition: ${alsokTheme.animation.transition};
        }

        .button-text {
          font-weight: bold;
        }

        .loading-overlay {
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
          animation: loadingShimmer 1.5s infinite;
          z-index: 1;
        }

        .login-button.loading .button-icon {
          animation: spin 1s linear infinite;
        }

        .login-button:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: ${alsokTheme.shadows.security};
          background: ${alsokTheme.primary.light};
        }

        .login-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        .test-accounts-section {
          margin-top: 30px;
          border-top: 1px solid #e0e0e0;
          padding-top: 20px;
        }

        .test-accounts-toggle {
          width: 100%;
          padding: 12px;
          background: ${alsokTheme.neutral.lightGray};
          border: 1px solid ${alsokTheme.neutral.gray};
          border-radius: ${alsokTheme.borderRadius.md};
          cursor: pointer;
          font-size: 14px;
          transition: ${alsokTheme.animation.transition};
          color: ${alsokTheme.neutral.darkGray};
        }

        .test-accounts-toggle:hover {
          background: ${alsokTheme.primary.light};
          color: ${alsokTheme.neutral.white};
          border-color: ${alsokTheme.primary.main};
        }

        .test-accounts-list {
          margin-top: 15px;
          border: 1px solid ${alsokTheme.neutral.lightGray};
          border-radius: ${alsokTheme.borderRadius.md};
          overflow: hidden;
        }

        .test-account-item {
          width: 100%;
          padding: 15px;
          background: ${alsokTheme.neutral.white};
          border: none;
          border-bottom: 1px solid ${alsokTheme.neutral.lightGray};
          cursor: pointer;
          text-align: left;
          transition: ${alsokTheme.animation.transition};
          display: flex;
          flex-direction: column;
          gap: 5px;
        }

        .test-account-item:hover {
          background: rgba(0, 102, 204, 0.05);
          border-left: 3px solid ${alsokTheme.primary.light};
        }

        .test-account-item:last-child {
          border-bottom: none;
        }

        .account-name {
          font-weight: bold;
          color: #333;
        }

        .account-role {
          font-size: 12px;
          color: #666;
        }

        .account-credentials {
          font-size: 11px;
          color: #999;
          font-family: monospace;
          background: #f5f5f5;
          padding: 4px 8px;
          border-radius: 4px;
          align-self: flex-start;
        }

        .quick-access {
          margin-top: 30px;
          border-top: 2px solid ${alsokTheme.primary.light};
          padding-top: 25px;
          text-align: center;
          background: linear-gradient(135deg, ${alsokTheme.neutral.white} 0%, ${alsokTheme.neutral.lightGray} 100%);
          border-radius: ${alsokTheme.borderRadius.lg};
          padding: 25px;
          box-shadow: ${alsokTheme.shadows.md};
          border: 2px solid ${alsokTheme.accent.success};
        }

        .quick-reception-button {
          width: 100%;
          padding: ${alsokTheme.spacing.lg} ${alsokTheme.spacing.xl};
          background: linear-gradient(135deg, ${alsokTheme.accent.success} 0%, #229954 100%);
          color: ${alsokTheme.neutral.white};
          border: 3px solid ${alsokTheme.accent.success};
          border-radius: ${alsokTheme.borderRadius.lg};
          font-size: ${alsokTheme.typography.fontSize.xl};
          font-weight: ${alsokTheme.typography.fontWeight.bold};
          cursor: pointer;
          transition: ${alsokTheme.animation.transition};
          font-family: ${alsokTheme.typography.fontFamily.primary};
          box-shadow: 0 6px 20px rgba(39, 174, 96, 0.4);
          position: relative;
          overflow: hidden;
          min-height: 60px;
          text-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }

        .quick-reception-button::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
          transition: left 0.6s ease;
        }
        
        .quick-reception-button:hover {
          background: linear-gradient(135deg, #1e7e34 0%, #196F3D 100%);
          transform: translateY(-3px) scale(1.02);
          box-shadow: 0 8px 25px rgba(39, 174, 96, 0.5);
          border-color: #229954;
        }
        
        .quick-reception-button:hover::before {
          left: 100%;
        }

        .quick-access-note {
          margin: 15px 0 0 0;
          font-size: ${alsokTheme.typography.fontSize.sm};
          color: ${alsokTheme.primary.main};
          font-weight: ${alsokTheme.typography.fontWeight.semibold};
          background: ${alsokTheme.neutral.white};
          padding: ${alsokTheme.spacing.sm} ${alsokTheme.spacing.md};
          border-radius: ${alsokTheme.borderRadius.md};
          border: 1px solid ${alsokTheme.primary.light};
          box-shadow: ${alsokTheme.shadows.sm};
        }

        .login-footer {
          background: ${alsokTheme.neutral.lightGray};
          padding: 20px 30px;
          text-align: center;
          color: ${alsokTheme.neutral.mediumGray};
          font-size: ${alsokTheme.typography.fontSize.xs};
          border-top: 1px solid ${themeHelpers.withOpacity(alsokTheme.primary.main, 0.1)};
        }

        .login-footer p {
          margin: 5px 0;
        }

        @keyframes securityPulse {
          0%, 100% {
            transform: scale(1);
            opacity: 1;
          }
          50% {
            transform: scale(1.1);
            opacity: 0.8;
          }
        }

        @keyframes gridMove {
          0% { transform: translateX(0) translateY(0); }
          100% { transform: translateX(10px) translateY(10px); }
        }

        @keyframes glowMove {
          0%, 100% { 
            transform: translateX(0) translateY(0) scale(1);
            opacity: 0.3;
          }
          33% { 
            transform: translateX(20px) translateY(-10px) scale(1.1);
            opacity: 0.4;
          }
          66% { 
            transform: translateX(-15px) translateY(15px) scale(0.9);
            opacity: 0.2;
          }
        }

        @keyframes borderGlow {
          0%, 100% { 
            opacity: 1;
            filter: brightness(1);
          }
          50% { 
            opacity: 0.8;
            filter: brightness(1.3);
          }
        }

        @keyframes textShimmer {
          0%, 100% { 
            background-position: 0% 50%;
          }
          50% { 
            background-position: 100% 50%;
          }
        }

        @keyframes floatParticle {
          0%, 100% {
            transform: translateY(0px) scale(1);
            opacity: 0.3;
          }
          50% {
            transform: translateY(-20px) scale(1.1);
            opacity: 0.6;
          }
        }

        .quick-access-header {
          margin-bottom: 20px;
        }
        
        .quick-access-header h3 {
          margin: 0 0 8px 0;
          color: ${alsokTheme.accent.success};
          font-size: ${alsokTheme.typography.fontSize.lg};
          font-weight: ${alsokTheme.typography.fontWeight.bold};
          text-align: center;
        }
        
        .quick-access-description {
          margin: 0;
          color: ${alsokTheme.neutral.mediumGray};
          font-size: ${alsokTheme.typography.fontSize.sm};
          text-align: center;
        }
        
        .button-icon {
          font-size: 24px;
          display: block;
          margin-bottom: 5px;
        }
        
        .button-main-text {
          display: block;
          font-size: ${alsokTheme.typography.fontSize.xl};
          font-weight: ${alsokTheme.typography.fontWeight.bold};
          margin-bottom: 2px;
        }
        
        .button-sub-text {
          display: block;
          font-size: ${alsokTheme.typography.fontSize.sm};
          opacity: 0.9;
          font-weight: ${alsokTheme.typography.fontWeight.medium};
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @keyframes loadingShimmer {
          0% { left: -100%; }
          100% { left: 100%; }
        }
        
        @keyframes bounce {
          0%, 20%, 50%, 80%, 100% {
            transform: translateY(0);
          }
          40% {
            transform: translateY(-10px);
          }
          60% {
            transform: translateY(-5px);
          }
        }

        @media (max-width: 600px) {
          .login-container {
            margin: 0;
            border-radius: 0;
            min-height: 100vh;
          }
          
          .login-header {
            padding: 30px 20px;
          }
          
          .login-form-container {
            padding: 30px 20px;
          }
          
          .logo-subtitle {
            font-size: 10px;
            letter-spacing: 0.5px;
          }
        }
        
        @media (max-width: 480px) {
          .logo-subtitle {
            font-size: 9px;
            letter-spacing: 0.3px;
          }
        }
        
        @media (min-width: 768px) {
          .logo-subtitle {
            font-size: 14px;
            letter-spacing: 1.2px;
          }
        }

        /* カスタムモーダルダイアログのスタイル */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.6);
          backdrop-filter: blur(5px);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 9999;
          animation: fadeIn 0.3s ease-out;
        }

        .modal-container {
          background: linear-gradient(135deg, 
            rgba(255, 255, 255, 0.98) 0%, 
            rgba(248, 250, 252, 0.95) 100%);
          border-radius: 20px;
          box-shadow: 
            0 20px 60px rgba(0, 0, 0, 0.2),
            0 0 0 1px rgba(255, 255, 255, 0.3);
          max-width: 480px;
          width: 90%;
          max-height: 80vh;
          overflow: hidden;
          animation: slideIn 0.4s ease-out;
          backdrop-filter: blur(15px);
          border: 2px solid ${alsokTheme.primary.light};
        }

        .modal-header {
          background: linear-gradient(135deg, 
            ${alsokTheme.primary.main} 0%, 
            ${alsokTheme.primary.dark} 100%);
          color: white;
          padding: 25px 30px;
          text-align: center;
          position: relative;
          overflow: hidden;
        }

        .modal-header::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
          animation: shimmer 3s infinite;
        }

        .modal-icon {
          font-size: 48px;
          margin-bottom: 10px;
          filter: drop-shadow(0 4px 8px rgba(0,0,0,0.2));
          animation: pulse 2s infinite;
        }

        .modal-title {
          font-size: 24px;
          font-weight: bold;
          margin: 0;
          text-shadow: 0 2px 4px rgba(0,0,0,0.2);
          letter-spacing: 1px;
        }

        .modal-content {
          padding: 30px;
        }

        .modal-description {
          font-size: 18px;
          font-weight: 600;
          color: ${alsokTheme.neutral.darkGray};
          text-align: center;
          margin: 0 0 25px 0;
        }

        .modal-warning {
          display: flex;
          align-items: center;
          gap: 12px;
          background: linear-gradient(135deg, 
            rgba(255, 193, 7, 0.1) 0%, 
            rgba(255, 152, 0, 0.1) 100%);
          border: 2px solid #ffc107;
          border-radius: 12px;
          padding: 15px 20px;
          margin-bottom: 20px;
        }

        .warning-icon {
          font-size: 24px;
          animation: warning-pulse 2s infinite;
        }

        .warning-title {
          font-weight: bold;
          color: #d68910;
          font-size: 16px;
          letter-spacing: 0.5px;
        }

        .modal-notes {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .modal-notes li {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 8px 0;
          font-size: 14px;
          color: ${alsokTheme.neutral.mediumGray};
          line-height: 1.5;
        }

        .modal-notes li::before {
          content: '•';
          color: ${alsokTheme.primary.main};
          font-weight: bold;
          font-size: 16px;
          flex-shrink: 0;
          margin-top: 2px;
        }

        .modal-actions {
          display: flex;
          gap: 15px;
          justify-content: center;
          padding: 0 30px 30px 30px;
        }

        .modal-button {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 15px 25px;
          border: none;
          border-radius: 12px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          min-height: 50px;
          position: relative;
          overflow: hidden;
        }

        .modal-button::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
          transition: left 0.6s ease;
        }

        .modal-button:hover::before {
          left: 100%;
        }

        .cancel-button {
          background: linear-gradient(135deg, #6c757d 0%, #5a6268 100%);
          color: white;
          border: 2px solid #6c757d;
        }

        .cancel-button:hover {
          background: linear-gradient(135deg, #5a6268 0%, #495057 100%);
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(108, 117, 125, 0.3);
        }

        .confirm-button {
          background: linear-gradient(135deg, ${alsokTheme.accent.success} 0%, #229954 100%);
          color: white;
          border: 2px solid ${alsokTheme.accent.success};
        }

        .confirm-button:hover {
          background: linear-gradient(135deg, #229954 0%, #1e7e34 100%);
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(39, 174, 96, 0.4);
        }

        .button-icon {
          font-size: 18px;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(-50px) scale(0.9);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        @keyframes shimmer {
          0% { left: -100%; }
          100% { left: 100%; }
        }

        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.1);
          }
        }

        @keyframes warning-pulse {
          0%, 100% {
            transform: scale(1);
            opacity: 1;
          }
          50% {
            transform: scale(1.1);
            opacity: 0.8;
          }
        }

        @media (max-width: 600px) {
          .modal-container {
            width: 95%;
            margin: 20px;
          }
          
          .modal-header {
            padding: 20px;
          }
          
          .modal-icon {
            font-size: 40px;
          }
          
          .modal-title {
            font-size: 20px;
          }
          
          .modal-content {
            padding: 20px;
          }
          
          .modal-actions {
            flex-direction: column;
            padding: 0 20px 20px 20px;
          }
          
          .modal-button {
            width: 100%;
          }
        }
      `}</style>
    </div>
  )
}

export default LoginScreen